/**
 * MinimizedChatToast — LinkedIn-style incoming message bar
 *
 * Shows sender avatar + name in a compact bar at the bottom of the screen.
 * Click opens the conversation; X dismisses.
 * The entire bar flashes on arrival and on each new message.
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { ChatAvatar } from './ChatAvatar';
import { useChatWidgetStore, type MessageToast } from '../store';

interface MinimizedChatToastProps {
  toast: MessageToast;
}

export function MinimizedChatToast({ toast }: MinimizedChatToastProps) {
  const { openChat, dismissToast } = useChatWidgetStore();
  const [flashing, setFlashing] = useState(true);

  // Re-trigger flash whenever messageCount changes
  useEffect(() => {
    setFlashing(true);
    const timer = setTimeout(() => setFlashing(false), 600);
    return () => clearTimeout(timer);
  }, [toast.messageCount]);

  return (
    <motion.button
      layout
      initial={{ y: 40, opacity: 0 }}
      animate={{
        y: 0,
        opacity: 1,
        backgroundColor: flashing
          ? ['#ffffff', '#F1F2F5', '#ffffff', '#F1F2F5'] // flash twice then stick
          : '#F1F2F5',
      }}
      exit={{ y: 40, opacity: 0 }}
      transition={{
        y: { type: 'spring', stiffness: 400, damping: 30 },
        opacity: { type: 'spring', stiffness: 400, damping: 30 },
        backgroundColor: flashing
          ? { duration: 0.6, times: [0, 0.25, 0.5, 1], ease: 'easeOut' }
          : { duration: 0 },
      }}
      onClick={() => openChat(toast.conversationId)}
      className="relative flex w-[280px] items-center gap-2.5 overflow-hidden rounded-t-lg px-3 py-2.5 shadow-lg ring-1 ring-black/5 cursor-pointer hover:bg-gray-50"
    >
      {/* Avatar with notification dot */}
      <div className="relative shrink-0">
        <ChatAvatar
          src={toast.senderAvatar}
          name={toast.senderName}
          size="h-7 w-7"
        />
        <span className="absolute -top-0.5 -end-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white" />
      </div>

      {/* Sender name */}
      <span className="min-w-0 flex-1 truncate text-start text-xs font-semibold text-gray-900">
        {toast.senderName}
      </span>

      {/* Unread count badge */}
      <AnimatePresence mode="popLayout">
        {toast.messageCount > 0 && (
          <motion.span
            key={toast.messageCount}
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 20 }}
            className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
          >
            {toast.messageCount > 99 ? '99+' : toast.messageCount}
          </motion.span>
        )}
      </AnimatePresence>

      {/* Dismiss button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          dismissToast(toast.conversationId);
        }}
        className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </motion.button>
  );
}
